'use client';

import React from 'react';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, DetailedHTMLProps } from 'react';
import type { ButtonProps as AriaButtonProps } from 'react-aria-components';
import { Button as AriaButton, Link as AriaLink } from 'react-aria-components';
import { cx, sortCx } from '@milanote/roma/utils/cx';
import { AppleLogo, FacebookLogo, GoogleLogo, TwitterLogo } from './social-logos';

const SEMANTIC_NAME = 'RomaSocialButton';

export const styles = sortCx({
    common: {
        root: [
            'group relative inline-flex h-max cursor-pointer items-center outline-primary justify-center whitespace-nowrap select-none transition duration-100 ease-linear before:absolute focus-visible:outline-2 focus-visible:-outline-offset-1',
            'disabled:cursor-not-allowed disabled:text-fg-disabled',
            '*:data-icon:pointer-events-none *:data-icon:size-5 *:data-icon:shrink-0 *:data-icon:transition-inherit-all',
        ].join(' '),
        icon: 'pointer-events-none size-5 shrink-0 transition-inherit-all',
    },

    sizes: {
        sm: {
            root: 'gap-3 rounded-xs px-2.5 py-1.5 text-sm font-semibold before:rounded-[5px] data-icon-only:p-1.5',
        },
        md: {
            root: 'gap-3 rounded-xs px-3.5 py-2.5 text-md font-semibold before:rounded-[5px] data-icon-only:p-2.5',
        },
        lg: {
            root: 'gap-4 rounded-sm px-4 py-2.5 text-lg font-semibold before:rounded-[7px] data-icon-only:p-3',
        },
    },

    colors: {
        gray: {
            root: 'bg-primary text-secondary ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover',
            icon: 'text-fg-quaternary group-hover:text-fg-quaternary_hover',
        },
        black: {
            root: 'bg-black text-white ring-1 ring-transparent ring-inset before:absolute before:inset-px before:border before:border-white/12 before:mask-b-from-0%',
            icon: '',
        },
        facebook: {
            root: 'bg-[#1877F2] text-white ring-1 ring-transparent ring-inset before:absolute before:inset-px before:border before:border-white/12 before:mask-b-from-0% hover:bg-[#0C63D4]',
            icon: '',
        },
    },
});

export type SocialType = 'google' | 'facebook' | 'apple' | 'twitter';

export interface CommonProps {
    social: SocialType;
    disabled?: boolean;
    theme?: 'brand' | 'color' | 'gray';
    size?: keyof typeof styles.sizes;
}

export interface ButtonProps
    extends CommonProps,
        DetailedHTMLProps<Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color' | 'slot'>, HTMLButtonElement> {
    slot?: AriaButtonProps['slot'];
}

export interface LinkProps
    extends CommonProps,
        DetailedHTMLProps<Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'color'>, HTMLAnchorElement> {}

export type SocialButtonProps = ButtonProps | LinkProps;

const getColorStyles = (social: SocialType, theme: CommonProps['theme']) => {
    if (theme === 'gray') {
        return styles.colors.gray;
    }

    if (theme === 'brand') {
        const socialToColor = {
            google: 'gray',
            facebook: 'facebook',
            apple: 'black',
            twitter: 'black',
        } as const;
        return styles.colors[socialToColor[social]];
    }

    return styles.colors.gray;
};

const getLogoComponent = (social: SocialType) => {
    const logos = {
        google: GoogleLogo,
        facebook: FacebookLogo,
        apple: AppleLogo,
        twitter: TwitterLogo,
    };
    return logos[social];
};

const getIconClassName = (social: SocialType, theme: CommonProps['theme'], colorStyles: any) => {
    if (theme === 'gray') {
        return colorStyles.icon;
    }

    if (theme === 'brand') {
        if (social === 'facebook' || social === 'apple' || social === 'twitter') {
            return 'text-white';
        }
    }

    if (theme === 'color') {
        if (social === 'apple' || social === 'twitter') {
            return 'text-alpha-black';
        }
    }

    return '';
};

const shouldUseColorfulLogo = (social: SocialType, theme: CommonProps['theme']) => {
    if (theme === 'brand' && social === 'google') {
        return true;
    }

    if (theme === 'color' && (social === 'google' || social === 'facebook')) {
        return true;
    }

    return false;
};

export const SocialButton = ({
    size = 'sm',
    theme = 'brand',
    social,
    className,
    children,
    disabled,
    ...otherProps
}: SocialButtonProps) => {
    const href = 'href' in otherProps ? otherProps.href : undefined;
    const Component = href ? AriaLink : AriaButton;

    const isIconOnly = !children;
    const colorStyles = getColorStyles(social, theme);
    const Logo = getLogoComponent(social);
    const iconClassName = getIconClassName(social, theme, colorStyles);
    const colorful = shouldUseColorfulLogo(social, theme);

    let props = {};

    if (href) {
        props = {
            ...otherProps,
            href: disabled ? undefined : href,
            // Since anchor elements do not support the `disabled` attribute and state,
            // we need to specify `data-rac` and `data-disabled` in order to be able
            // to use the `disabled:` selector in classes.
            ...(disabled ? { 'data-rac': true, 'data-disabled': true } : {}),
        };
    } else {
        props = {
            ...otherProps,
            type: otherProps.type || 'button',
            isDisabled: disabled,
        };
    }

    return (
        <Component
            isDisabled={disabled}
            {...props}
            data-icon-only={isIconOnly ? true : undefined}
            className={cx(SEMANTIC_NAME, styles.common.root, styles.sizes[size].root, colorStyles.root, className)}
        >
            <Logo data-icon className={cx(styles.common.icon, iconClassName)} colorful={colorful || undefined} />

            {children}
        </Component>
    );
};

SocialButton.displayName = SEMANTIC_NAME;
